import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faAngleRight,
  faBuilding,
  faCheck,
  faDownload,
  faFilter,
  faPlus,
  faSearch,
  faSyncAlt,
  faTimes,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import {
  getCreditFileAll,
  getCreditFileCorporates,
  getCreditFileList,
  insertCreditFileLog,
} from "../../services/settlementApi";

const CORP_INSURANCE = [
  "1ROTARY",
  "EPE",
  "FIRST LIFE",
  "FPG",
  "HC&D",
  "HPS ICT INC.",
  "LAWINA",
  "Le Price",
  "POLARIS FINANCIAL",
  "RAM",
  "RAROCO",
];

const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50, 100];

const formatCurrency = (value) => {
  const numeric = Number(value ?? 0);
  if (Number.isNaN(numeric)) return "PHP 0.00";

  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
};

const parseNumeric = (value) => {
  if (value === null || value === undefined) return 0;
  const normalized = String(value).replace(/,/g, "").trim();
  const numeric = Number(normalized);
  return Number.isFinite(numeric) ? numeric : 0;
};

const toFileAmount = (value) => {
  const numeric = parseNumeric(value);
  return numeric.toFixed(2).replace(".", "");
};

const formatDateMmDdYyyy = (date = new Date()) => {
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = String(date.getFullYear());
  return `${mm}${dd}${yyyy}`;
};

const normalizeEnye = (value) =>
  String(value ?? "")
    .replace(/\u00c3\u0192\u00c2\u00b1/g, "n")
    .replace(/\u00c3\u0192\u00e2\u20ac\u02dc/g, "N")
    .replace(/\u00c3\u00b1/g, "n")
    .replace(/\u00c3[\u0091\u2018]/g, "N")
    .normalize("NFD")
    .replace(/N\u0303/g, "N")
    .replace(/n\u0303/g, "n")
    .normalize("NFC");

const sanitizeLineText = (text) =>
  normalizeEnye(text)
    .replace(/Ã±/g, "n")
    .replace(/Ã‘/g, "N")
    .replace(/&/g, "and")
    .replace(/'/g, "");

const sanitizeAccountName = (value) =>
  normalizeEnye(value)
    .substring(0, 50)
    .replace(/\|/g, "")
    .replace(/-/g, " ");

const escapeCsvCell = (value) => {
  const raw = normalizeEnye(value);
  return `"${raw.replace(/"/g, '""')}"`;
};

const stickyActionHeaderClassName =
  "sticky left-0 z-40 w-[92px] min-w-[92px] bg-slate-100 px-2 py-2 text-left font-semibold text-slate-600 border-r border-slate-200";
const stickyActionCellBaseClassName =
  "sticky left-0 z-20 w-[92px] min-w-[92px] bg-white px-2 py-1.5 whitespace-nowrap border-r border-slate-200";

const getPrnLabel = (paymentRefNo) => {
  const prn = String(paymentRefNo ?? "");
  if (!prn) return "";

  const firstChar = prn.charAt(0);
  const parts = prn.split("-");
  const suffix = parts[parts.length - 1] ?? "";
  return `${firstChar} ${suffix}`.trim();
};

const buildCorpOptions = (corporateRows) => {
  const options = [];
  const seen = new Set();

  const addOption = (value, label) => {
    const normalizedValue = String(value ?? "").trim();
    const normalizedLabel = String(label ?? "").trim();

    if (!normalizedValue || !normalizedLabel) return;

    const labelLower = normalizedLabel.toLowerCase();
    if (labelLower.includes("test") || labelLower.includes("-main")) return;

    const key = `${normalizedValue}__${normalizedLabel}`;
    if (seen.has(key)) return;

    seen.add(key);
    options.push({ value: normalizedValue, label: normalizedLabel });
  };

  CORP_INSURANCE.forEach((corp) => addOption(corp, corp));
  (corporateRows ?? []).forEach((row) => addOption(row.corp_code, row.corp_name));

  return options.sort((a, b) => a.label.localeCompare(b.label));
};

const matchesRowByTerm = (row, term, fields) => {
  if (!term) return true;
  const lower = term.toLowerCase();

  return fields.some((field) => String(row[field] ?? "").toLowerCase().includes(lower));
};

const getStatusBadgeMeta = (statusValue) => {
  const rawStatus = String(statusValue ?? "").trim();
  const normalized = rawStatus.toLowerCase();

  if (normalized === "paid") {
    return {
      label: "Paid",
      className: "bg-cyan-100 text-cyan-700 border-cyan-300",
    };
  }

  if (normalized === "incomplete" || normalized === "imcomplete") {
    return {
      label: "Incomplete",
      className: "bg-amber-100 text-amber-700 border-amber-300",
    };
  }

  if (!rawStatus) {
    return {
      label: "-",
      className: "bg-slate-100 text-slate-500 border-slate-200",
    };
  }

  return {
    label: rawStatus,
    className: "bg-slate-100 text-slate-700 border-slate-300",
  };
};

const CreditFilePage = () => {
  const [loading, setLoading] = useState(false);
  const [loadingSelectAll, setLoadingSelectAll] = useState(false);
  const [loadingGenerateType, setLoadingGenerateType] = useState("");
  const [submittingConfirmAction, setSubmittingConfirmAction] = useState(false);

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    total_unfiltered: 0,
    current_page: 1,
    last_page: 1,
    from: 0,
    to: 0,
    per_page: PAGE_SIZE_OPTIONS[0],
  });

  const [corporateOptions, setCorporateOptions] = useState([]);
  const [isCorporateModalOpen, setIsCorporateModalOpen] = useState(false);
  const [corporateModalSearch, setCorporateModalSearch] = useState("");
  const [corporateDraftCodes, setCorporateDraftCodes] = useState([]);

  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [searchBy, setSearchBy] = useState("all");

  const [selectedCorporateCodes, setSelectedCorporateCodes] = useState([]);
  const [appliedCorporateCodes, setAppliedCorporateCodes] = useState([]);

  const [dateStartInput, setDateStartInput] = useState("");
  const [dateEndInput, setDateEndInput] = useState("");
  const [appliedDateStart, setAppliedDateStart] = useState("");
  const [appliedDateEnd, setAppliedDateEnd] = useState("");

  const [sortBy, setSortBy] = useState("credited_date");
  const [sortDirection, setSortDirection] = useState("desc");

  const [page, setPage] = useState(1);
  const [prnPerPage, setPrnPerPage] = useState(PAGE_SIZE_OPTIONS[0]);

  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedSearch, setSelectedSearch] = useState("");
  const [selectedPage, setSelectedPage] = useState(1);
  const [selectedPerPage, setSelectedPerPage] = useState(PAGE_SIZE_OPTIONS[0]);

  const [confirmActionType, setConfirmActionType] = useState("");

  const selectedRowIds = useMemo(
    () => new Set(selectedRows.map((row) => String(row.tbl_id))),
    [selectedRows]
  );

  const selectedNetTotal = useMemo(
    () => selectedRows.reduce((sum, row) => sum + parseNumeric(row.net_payable), 0),
    [selectedRows]
  );

  const selectedNetTotalCompact = useMemo(
    () => selectedNetTotal.toFixed(2).replace(".", ""),
    [selectedNetTotal]
  );

  const filteredSelectedRows = useMemo(() => {
    const term = selectedSearch.trim().toLowerCase();
    if (!term) return selectedRows;

    return selectedRows.filter((row) =>
      matchesRowByTerm(row, term, [
        "payment_ref_no",
        "provider_name",
        "corp_name",
        "city",
        "bank_name",
        "check_no",
        "cv_no",
        "status",
        "deposit_slip_file",
      ])
    );
  }, [selectedRows, selectedSearch]);

  const selectedTotalPages = useMemo(() => {
    if (filteredSelectedRows.length === 0) return 1;
    return Math.ceil(filteredSelectedRows.length / selectedPerPage);
  }, [filteredSelectedRows.length, selectedPerPage]);

  const pagedSelectedRows = useMemo(() => {
    const start = (selectedPage - 1) * selectedPerPage;
    return filteredSelectedRows.slice(start, start + selectedPerPage);
  }, [filteredSelectedRows, selectedPage, selectedPerPage]);

  useEffect(() => {
    if (selectedPage > selectedTotalPages) {
      setSelectedPage(selectedTotalPages);
    }
  }, [selectedPage, selectedTotalPages]);

  const filteredCorporateOptions = useMemo(() => {
    const term = corporateModalSearch.trim().toLowerCase();
    if (!term) return corporateOptions;

    return corporateOptions.filter((option) => {
      const label = String(option.label ?? "").toLowerCase();
      const value = String(option.value ?? "").toLowerCase();
      return label.includes(term) || value.includes(term);
    });
  }, [corporateOptions, corporateModalSearch]);

  const corporateDraftSet = useMemo(() => new Set(corporateDraftCodes), [corporateDraftCodes]);

  const selectedSummary = useMemo(() => {
    const corpSet = new Set();
    const prnPreview = [];

    selectedRows.forEach((row) => {
      const corpName = String(row.corp_name ?? "").trim();
      if (corpName) {
        corpSet.add(corpName);
      }

      if (prnPreview.length < 5) {
        const prn = String(row.payment_ref_no ?? "").trim();
        if (prn) {
          prnPreview.push(prn);
        }
      }
    });

    return {
      count: selectedRows.length,
      corpCount: corpSet.size,
      total: selectedNetTotal,
      totalCompact: selectedNetTotalCompact,
      prnPreview,
    };
  }, [selectedRows, selectedNetTotal, selectedNetTotalCompact]);

  const confirmActionMeta = useMemo(() => {
    if (confirmActionType === "pesonet") {
      return {
        title: "Confirm Generate PESONET",
        subtitle: "This will create a PESONET text file from all selected PRNs.",
        confirmLabel: loadingGenerateType === "pesonet" ? "Generating..." : "Generate PESONET",
      };
    }

    if (confirmActionType === "instapay") {
      return {
        title: "Confirm Generate INSTAPAY",
        subtitle: "This will create an INSTAPAY text file from all selected PRNs.",
        confirmLabel: loadingGenerateType === "instapay" ? "Generating..." : "Generate INSTAPAY",
      };
    }

    return {
      title: "Confirm Download CSV",
      subtitle: "This will download a CSV file for all selected PRNs.",
      confirmLabel: "Download CSV",
    };
  }, [confirmActionType, loadingGenerateType]);

  const fetchCorporateOptions = useCallback(async () => {
    try {
      const response = await getCreditFileCorporates();
      const options = buildCorpOptions(response.data.data ?? []);
      setCorporateOptions(options);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load corporate filters.");
    }
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getCreditFileList({
        page,
        per_page: prnPerPage,
        search: appliedSearch,
        search_by: searchBy,
        corp_codes: appliedCorporateCodes.join(","),
        start_date: appliedDateStart,
        end_date: appliedDateEnd,
        sort_by: sortBy,
        sort_direction: sortDirection,
      });

      setRows(response.data.data ?? []);
      setPagination(
        response.data.pagination ?? {
          total: 0,
          total_unfiltered: 0,
          current_page: 1,
          last_page: 1,
          from: 0,
          to: 0,
          per_page: prnPerPage,
        }
      );
    } catch (error) {
      console.error(error);
      toast.error("Failed to load credit file records.");
    } finally {
      setLoading(false);
    }
  }, [
    page,
    prnPerPage,
    appliedSearch,
    searchBy,
    appliedCorporateCodes,
    appliedDateStart,
    appliedDateEnd,
    sortBy,
    sortDirection,
  ]);

  useEffect(() => {
    fetchCorporateOptions();
  }, [fetchCorporateOptions]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleApplyFilters = () => {
    setAppliedSearch(searchInput.trim());
    setAppliedCorporateCodes(selectedCorporateCodes);
    setAppliedDateStart(dateStartInput);
    setAppliedDateEnd(dateEndInput);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setAppliedSearch("");
    setSearchBy("all");

    setSelectedCorporateCodes([]);
    setAppliedCorporateCodes([]);

    setDateStartInput("");
    setDateEndInput("");
    setAppliedDateStart("");
    setAppliedDateEnd("");

    setSortBy("credited_date");
    setSortDirection("desc");
    setPage(1);
  };

  const toggleSort = (column) => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDirection("desc");
    }
    setPage(1);
  };

  const addSelectedRow = (rowData) => {
    if (!rowData) return;

    if (selectedRowIds.has(String(rowData.tbl_id))) {
      setSelectedRows((prev) => prev.filter((row) => String(row.tbl_id) !== String(rowData.tbl_id)));
      return;
    }

    let rowToInsert = { ...rowData };
    const city = String(rowToInsert.city ?? "").trim();

    if (!city) {
      const inputCity = window.prompt(`Enter city for ${rowToInsert.provider_name || "selected provider"}`);
      if (!inputCity || !String(inputCity).trim()) {
        return;
      }
      rowToInsert.city = String(inputCity).trim();
    }

    setSelectedRows((prev) => [...prev, rowToInsert]);
  };

  const removeSelectedRow = (tblId) => {
    setSelectedRows((prev) => prev.filter((row) => String(row.tbl_id) !== String(tblId)));
  };

  const removeAllSelected = () => {
    if (selectedRows.length === 0) return;

    const confirmed = window.confirm("Remove all selected PRNs?");
    if (!confirmed) return;

    setSelectedRows([]);
    setSelectedSearch("");
    setSelectedPage(1);
  };

  const handleSelectAllFiltered = async () => {
    setLoadingSelectAll(true);

    try {
      const response = await getCreditFileAll({
        search: appliedSearch,
        search_by: searchBy,
        corp_codes: appliedCorporateCodes.join(","),
        start_date: appliedDateStart,
        end_date: appliedDateEnd,
        sort_by: "due_date",
        sort_direction: "desc",
      });

      const allRows = response.data.data ?? [];
      setSelectedRows(allRows);
      setSelectedSearch("");
      setSelectedPage(1);

      toast.success(`Selected ${allRows.length} PRN(s).`);
    } catch (error) {
      console.error(error);
      toast.error("Failed to select all filtered PRNs.");
    } finally {
      setLoadingSelectAll(false);
    }
  };

  const downloadTextFile = (filename, content) => {
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");

    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();

    anchor.remove();
    URL.revokeObjectURL(url);
  };

  const executeGenerate = async (type) => {
    if (!selectedRows.length) {
      toast.warning("Select at least one PRN.");
      return false;
    }

    const mode = type === "instapay" ? "instapay" : "pesonet";
    setLoadingGenerateType(mode);

    try {
      let rowCount = 0;
      let netTotal = 0;

      const lines = selectedRows.map((row) => {
        rowCount += 1;
        netTotal += parseNumeric(row.net_payable);

        const bankCode = mode === "instapay" ? row.bank_code_instapay : row.bank_code_pesonet;
        const paymentRefLabel = getPrnLabel(row.payment_ref_no);

        return [
          "D",
          sanitizeAccountName(row.bank_acct_name),
          String(row.bank_acct_no ?? "").replace(/-/g, ""),
          String(row.city ?? "").toUpperCase(),
          String(bankCode ?? ""),
          String(bankCode ?? ""),
          String(bankCode ?? ""),
          toFileAmount(row.net_payable),
          "",
          "",
          paymentRefLabel,
          paymentRefLabel,
          "",
          "",
          "",
          "",
          "",
          "",
          "",
        ].join("|");
      });

      const txtContent = sanitizeLineText(lines.join("\n"));

      const logResponse = await insertCreditFileLog({ txt_content: txtContent });
      const todayCount = Number(logResponse.data?.data?.count_today ?? 0);
      const paddedCount = String(todayCount).padStart(3, "0");

      const filePrefix = mode === "instapay" ? "INSTAPAY" : "PESONET";
      const filename = `${filePrefix}_${formatDateMmDdYyyy()}_${paddedCount}`;
      const netTotalForHeader = netTotal.toFixed(2).replace(".", "");
      const header = `HDR||${rowCount}|${netTotalForHeader}|EPCS|PHP|${filename}`;

      downloadTextFile(`${filename}.txt`, `${header}\n${txtContent}`);
      toast.success(`${mode.toUpperCase()} file generated.`);
      return true;
    } catch (error) {
      console.error(error);
      toast.error(`Failed to generate ${mode.toUpperCase()} file.`);
      return false;
    } finally {
      setLoadingGenerateType("");
    }
  };

  const executeDownloadSelectedCsv = () => {
    if (!selectedRows.length) {
      toast.warning("No selected PRNs to export.");
      return false;
    }

    const headers = [
      "Date Credited",
      "Payment Ref #",
      "Corporate",
      "Provider",
      "Bank Name",
      "Bank Acct. Name",
      "Bank Acct. #",
      "Bank Code: Pesonet",
      "Bank Code: Instapay",
      "Provider Address",
      "City",
      "PPD Term",
      "Due Date",
      "Net Payable",
      "Check Number",
      "CV#",
      "Provider OR No.",
      "Status",
      "Deposit Slip File",
      "PPD",
    ];

    const rowsForCsv = selectedRows.map((row) => [
      row.credited_date,
      row.payment_ref_no,
      row.corp_name,
      row.provider_name,
      row.bank_name,
      row.bank_acct_name,
      row.bank_acct_no,
      row.bank_code_pesonet,
      row.bank_code_instapay,
      row.provider_address,
      row.city,
      row.ppd_term,
      row.due_date,
      row.net_payable,
      row.check_no,
      row.cv_no,
      row.provider_or_no,
      row.status,
      row.deposit_slip_file,
      row.ppd,
    ]);

    const csvContent = [
      headers.map(escapeCsvCell).join(","),
      ...rowsForCsv.map((cells) => cells.map(escapeCsvCell).join(",")),
    ].join("\n");

    const filename = `credit_file_selected_prns_${Date.now()}.csv`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");

    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();

    anchor.remove();
    URL.revokeObjectURL(url);
    toast.success("Selected PRNs CSV downloaded.");
    return true;
  };

  const openCorporateModal = () => {
    setCorporateDraftCodes(selectedCorporateCodes);
    setCorporateModalSearch("");
    setIsCorporateModalOpen(true);
  };

  const applyCorporateSelection = () => {
    setSelectedCorporateCodes(corporateDraftCodes);
    setIsCorporateModalOpen(false);
  };

  const toggleCorporateDraftCode = (code) => {
    setCorporateDraftCodes((prev) => {
      if (prev.includes(code)) {
        return prev.filter((value) => value !== code);
      }

      return [...prev, code];
    });
  };

  const selectAllFilteredCorporateDraft = () => {
    setCorporateDraftCodes((prev) => {
      const merged = new Set(prev);
      filteredCorporateOptions.forEach((option) => merged.add(option.value));
      return Array.from(merged);
    });
  };

  const clearFilteredCorporateDraft = () => {
    const filteredValues = new Set(filteredCorporateOptions.map((option) => option.value));
    setCorporateDraftCodes((prev) => prev.filter((code) => !filteredValues.has(code)));
  };

  const openConfirmAction = (type) => {
    if (!selectedRows.length) {
      toast.warning("Select at least one PRN.");
      return;
    }

    setConfirmActionType(type);
  };

  const closeConfirmAction = () => {
    if (submittingConfirmAction) return;
    setConfirmActionType("");
  };

  const submitConfirmAction = async () => {
    if (!confirmActionType) return;

    setSubmittingConfirmAction(true);

    try {
      let success = false;

      if (confirmActionType === "pesonet") {
        success = await executeGenerate("pesonet");
      } else if (confirmActionType === "instapay") {
        success = await executeGenerate("instapay");
      } else if (confirmActionType === "download_csv") {
        success = executeDownloadSelectedCsv();
      }

      if (success) {
        setConfirmActionType("");
      }
    } finally {
      setSubmittingConfirmAction(false);
    }
  };

  const leftTotalPages = pagination.last_page || 1;
  const selectedFrom = filteredSelectedRows.length ? (selectedPage - 1) * selectedPerPage + 1 : 0;
  const selectedTo = Math.min(selectedPage * selectedPerPage, filteredSelectedRows.length);

  return (
    <div className="p-4 min-h-screen bg-slate-50">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-slate-800">Credit File</h1>
        <p className="text-sm text-slate-500">Provider Payment &gt; Credit File</p>
      </div>

      <div className="bg-white rounded-2xl shadow border border-slate-200 p-4 mb-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div>
            <h2 className="text-sm font-bold text-slate-800">Filters</h2>
            <p className="text-xs text-slate-500">Set search, corporates, and date credited range.</p>
          </div>

          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span className="inline-flex items-center px-2 py-1 rounded-full bg-slate-100 border border-slate-200">
              Applied corporates: {appliedCorporateCodes.length}
            </span>
            <span className="inline-flex items-center px-2 py-1 rounded-full bg-slate-100 border border-slate-200">
              PRN rows per page: {prnPerPage}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-end">
          <div className="lg:col-span-2">
            <label className="block text-xs font-semibold text-slate-600 mb-1">Search By</label>
            <select
              value={searchBy}
              onChange={(event) => setSearchBy(event.target.value)}
              className="w-full border border-slate-300 rounded-lg px-2 py-2 text-sm bg-white"
            >
              <option value="all">All</option>
              <option value="check_no">Check Number</option>
              <option value="provider_name">Provider</option>
              <option value="payment_ref">Payment Ref #</option>
            </select>
          </div>

          <div className="lg:col-span-3">
            <label className="block text-xs font-semibold text-slate-600 mb-1">Corporate (multiple)</label>
            <button
              type="button"
              onClick={openCorporateModal}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white hover:bg-slate-50 flex items-center justify-between"
            >
              <span className="truncate pr-2">
                {selectedCorporateCodes.length
                  ? `${selectedCorporateCodes.length} corporate(s) selected`
                  : "Select corporates"}
              </span>
              <FontAwesomeIcon icon={faBuilding} className="text-slate-500" />
            </button>
          </div>

          <div className="lg:col-span-2">
            <label className="block text-xs font-semibold text-slate-600 mb-1">Date Credited From</label>
            <input
              type="date"
              value={dateStartInput}
              onChange={(event) => setDateStartInput(event.target.value)}
              className="w-full border border-slate-300 rounded-lg px-2 py-2 text-sm"
            />
          </div>

          <div className="lg:col-span-2">
            <label className="block text-xs font-semibold text-slate-600 mb-1">Date Credited To</label>
            <input
              type="date"
              value={dateEndInput}
              onChange={(event) => setDateEndInput(event.target.value)}
              className="w-full border border-slate-300 rounded-lg px-2 py-2 text-sm"
            />
          </div>

          <div className="lg:col-span-3">
            <label className="block text-xs font-semibold text-slate-600 mb-1">Rows Per Page (PRNs)</label>
            <select
              value={prnPerPage}
              onChange={(event) => {
                const value = Number(event.target.value);
                setPrnPerPage(value);
                setPage(1);
              }}
              className="w-full border border-slate-300 rounded-lg px-2 py-2 text-sm bg-white"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={`prn-page-size-${size}`} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleApplyFilters}
            className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <FontAwesomeIcon icon={faFilter} className="mr-1" /> Apply Filters
          </button>

          <button
            type="button"
            onClick={handleClearFilters}
            className="px-3 py-2 text-sm bg-rose-600 text-white rounded-lg hover:bg-rose-700"
          >
            <FontAwesomeIcon icon={faTimes} className="mr-1" /> Clear
          </button>

          <button
            type="button"
            onClick={fetchRows}
            className="px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg hover:bg-slate-100"
          >
            <FontAwesomeIcon icon={faSyncAlt} className="mr-1" /> Refresh
          </button>
        </div>

        {!!selectedCorporateCodes.length && (
          <div className="mt-3 flex flex-wrap gap-2">
            {selectedCorporateCodes.slice(0, 8).map((code) => (
              <span
                key={`selected-corp-chip-${code}`}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-sky-50 text-sky-700 border border-sky-200"
              >
                {code}
              </span>
            ))}
            {selectedCorporateCodes.length > 8 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-slate-100 text-slate-600 border border-slate-200">
                +{selectedCorporateCodes.length - 8} more
              </span>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl shadow border border-slate-200 overflow-hidden">
          <div className="p-3 border-b border-slate-200 flex items-center justify-between gap-2 flex-wrap">
            <div>
              <h2 className="text-sm font-bold text-slate-800">PRNs</h2>
              <p className="text-xs text-slate-500">Available PRNs from current filters</p>
            </div>

            <button
              type="button"
              onClick={handleSelectAllFiltered}
              disabled={loadingSelectAll}
              className="px-2.5 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faCheck} className="mr-1" />
              {loadingSelectAll ? "Selecting..." : "Select All"}
            </button>
          </div>

          <div className="p-3 border-b border-slate-200 bg-slate-50">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
              <div className="md:col-span-9">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Search PRNs</label>
                <div className="flex">
                  <input
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder="Search PRN, provider, corp, city..."
                    className="w-full border border-slate-300 rounded-l-lg px-2 py-2 text-sm"
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        handleApplyFilters();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleApplyFilters}
                    className="px-3 border border-l-0 border-slate-300 rounded-r-lg bg-blue-600 text-white hover:bg-blue-700"
                  >
                    <FontAwesomeIcon icon={faSearch} />
                  </button>
                </div>
              </div>

              <div className="md:col-span-3">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Page</label>
                <div className="h-[38px] flex items-center text-sm text-slate-600 px-2 border border-slate-200 rounded-lg bg-white">
                  {page} / {leftTotalPages}
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-auto max-h-[560px]">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-100 sticky top-0 z-30">
                <tr>
                  <th className={stickyActionHeaderClassName}>Action</th>
                  <th
                    className="px-2 py-2 text-left font-semibold text-slate-600 cursor-pointer"
                    onClick={() => toggleSort("credited_date")}
                  >
                    Date Credited
                  </th>
                  <th
                    className="px-2 py-2 text-left font-semibold text-slate-600 cursor-pointer"
                    onClick={() => toggleSort("payment_ref_no")}
                  >
                    Payment Ref #
                  </th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600">Corporate</th>
                  <th
                    className="px-2 py-2 text-left font-semibold text-slate-600 cursor-pointer"
                    onClick={() => toggleSort("provider_name")}
                  >
                    Provider
                  </th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600">Bank</th>
                  <th
                    className="px-2 py-2 text-left font-semibold text-slate-600 cursor-pointer"
                    onClick={() => toggleSort("check_no")}
                  >
                    Check Number
                  </th>
                  <th
                    className="px-2 py-2 text-left font-semibold text-slate-600 cursor-pointer"
                    onClick={() => toggleSort("cv_no")}
                  >
                    CV#
                  </th>
                  <th
                    className="px-2 py-2 text-left font-semibold text-slate-600 cursor-pointer"
                    onClick={() => toggleSort("status")}
                  >
                    Status
                  </th>
                  <th
                    className="px-2 py-2 text-left font-semibold text-slate-600 cursor-pointer"
                    onClick={() => toggleSort("deposit_slip_file")}
                  >
                    Deposit Slip File
                  </th>
                  <th className="px-2 py-2 text-right font-semibold text-slate-600">Net Payable</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={11} className="px-2 py-8 text-center text-slate-400">
                      Loading records...
                    </td>
                  </tr>
                )}

                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={11} className="px-2 py-8 text-center text-slate-400">
                      No records found.
                    </td>
                  </tr>
                )}

                {!loading &&
                  rows.map((row) => {
                    const isSelected = selectedRowIds.has(String(row.tbl_id));

                    return (
                      <tr key={row.tbl_id} className="group border-t border-slate-100 hover:bg-sky-50">
                        <td className={`${stickyActionCellBaseClassName} group-hover:bg-sky-50`}>
                          <button
                            type="button"
                            onClick={() => addSelectedRow(row)}
                            className={`px-2 py-1 rounded text-[11px] ${
                              isSelected
                                ? "bg-rose-100 text-rose-700 border border-rose-300"
                                : "bg-blue-100 text-blue-700 border border-blue-300"
                            }`}
                          >
                            <FontAwesomeIcon icon={isSelected ? faTimes : faPlus} className="mr-1" />
                            {isSelected ? "Remove" : "Select"}
                          </button>
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{row.credited_date || ""}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{row.payment_ref_no || ""}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{row.corp_name || ""}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap max-w-[180px] truncate" title={row.provider_name || ""}>
                          {row.provider_name || ""}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{row.bank_name || ""}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{row.check_no || ""}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{row.cv_no || ""}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">
                          {(() => {
                            const statusBadge = getStatusBadgeMeta(row.status);
                            return (
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${statusBadge.className}`}
                              >
                                {statusBadge.label}
                              </span>
                            );
                          })()}
                        </td>
                        <td
                          className="px-2 py-1.5 whitespace-nowrap max-w-[220px] truncate"
                          title={row.deposit_slip_file || ""}
                        >
                          {row.deposit_slip_file || ""}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap text-right font-medium">
                          {formatCurrency(row.net_payable)}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          <div className="px-3 py-2 border-t border-slate-200 flex items-center justify-between text-xs text-slate-600 flex-wrap gap-2">
            <span>
              Showing {pagination.from ?? 0} to {pagination.to ?? 0} of {pagination.total ?? 0} entries
              (filtered from {pagination.total_unfiltered ?? 0} total entries)
            </span>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page <= 1}
                className="px-2 py-1 border border-slate-300 rounded-lg disabled:opacity-50 hover:bg-slate-100"
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>

              <span className="px-2">
                Page {page} / {leftTotalPages}
              </span>

              <button
                type="button"
                onClick={() => setPage((prev) => Math.min(leftTotalPages, prev + 1))}
                disabled={page >= leftTotalPages}
                className="px-2 py-1 border border-slate-300 rounded-lg disabled:opacity-50 hover:bg-slate-100"
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow border border-slate-200 overflow-hidden">
          <div className="p-3 border-b border-slate-200 flex items-center justify-between gap-2 flex-wrap">
            <div>
              <h2 className="text-sm font-bold text-slate-800">Selected PRNs</h2>
              <p className="text-xs text-slate-500">
                Total: <span className="font-semibold text-slate-700">{formatCurrency(selectedNetTotal)} | {selectedNetTotalCompact}</span>
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => openConfirmAction("pesonet")}
                disabled={!selectedRows.length || loadingGenerateType !== "" || submittingConfirmAction}
                className="px-2.5 py-1.5 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faCheck} className="mr-1" />
                {loadingGenerateType === "pesonet" ? "Generating..." : "Generate PESONET"}
              </button>

              <button
                type="button"
                onClick={() => openConfirmAction("instapay")}
                disabled={!selectedRows.length || loadingGenerateType !== "" || submittingConfirmAction}
                className="px-2.5 py-1.5 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faCheck} className="mr-1" />
                {loadingGenerateType === "instapay" ? "Generating..." : "Generate INSTAPAY"}
              </button>

              <button
                type="button"
                onClick={() => openConfirmAction("download_csv")}
                disabled={!selectedRows.length || submittingConfirmAction || loadingGenerateType !== ""}
                className="px-2.5 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faDownload} className="mr-1" /> Download CSV
              </button>

              <button
                type="button"
                onClick={removeAllSelected}
                disabled={!selectedRows.length || loadingGenerateType !== ""}
                className="px-2.5 py-1.5 text-xs bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faTrash} className="mr-1" /> Remove All
              </button>
            </div>
          </div>

          <div className="p-3 border-b border-slate-200 bg-slate-50">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
              <div className="md:col-span-7">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Search Selected PRNs</label>
                <div className="flex">
                  <input
                    value={selectedSearch}
                    onChange={(event) => {
                      setSelectedSearch(event.target.value);
                      setSelectedPage(1);
                    }}
                    placeholder="Search PRN, provider, corp, city, check, CV, status..."
                    className="w-full border border-slate-300 rounded-l-lg px-2 py-2 text-sm"
                  />
                  <span className="px-3 border border-l-0 border-slate-300 rounded-r-lg bg-white text-slate-500 flex items-center">
                    <FontAwesomeIcon icon={faSearch} />
                  </span>
                </div>
              </div>

              <div className="md:col-span-3">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Rows Per Page</label>
                <select
                  value={selectedPerPage}
                  onChange={(event) => {
                    const value = Number(event.target.value);
                    setSelectedPerPage(value);
                    setSelectedPage(1);
                  }}
                  className="w-full border border-slate-300 rounded-lg px-2 py-2 text-sm bg-white"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={`selected-page-size-${size}`} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Page</label>
                <div className="h-[38px] flex items-center text-sm text-slate-600 px-2 border border-slate-200 rounded-lg bg-white">
                  {selectedPage} / {selectedTotalPages}
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-auto max-h-[560px]">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-100 sticky top-0 z-30">
                <tr>
                  <th className={stickyActionHeaderClassName}>Action</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600">Date Credited</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600">Payment Ref #</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600">Corporate</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600">Provider</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600">Bank</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600">City</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600">Check Number</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600">CV#</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600">Status</th>
                  <th className="px-2 py-2 text-left font-semibold text-slate-600">Deposit Slip File</th>
                  <th className="px-2 py-2 text-right font-semibold text-slate-600">Net Payable</th>
                </tr>
              </thead>
              <tbody>
                {filteredSelectedRows.length === 0 && (
                  <tr>
                    <td colSpan={12} className="px-2 py-8 text-center text-slate-400">
                      No selected PRNs.
                    </td>
                  </tr>
                )}

                {filteredSelectedRows.length > 0 &&
                  pagedSelectedRows.map((row) => (
                    <tr key={row.tbl_id} className="group border-t border-slate-100 hover:bg-rose-50">
                      <td className={`${stickyActionCellBaseClassName} group-hover:bg-rose-50`}>
                        <button
                          type="button"
                          onClick={() => removeSelectedRow(row.tbl_id)}
                          className="px-2 py-1 rounded text-[11px] bg-rose-100 text-rose-700 border border-rose-300"
                        >
                          <FontAwesomeIcon icon={faTimes} className="mr-1" /> Remove
                        </button>
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{row.credited_date || ""}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{row.payment_ref_no || ""}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{row.corp_name || ""}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap max-w-[180px] truncate" title={row.provider_name || ""}>
                        {row.provider_name || ""}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{row.bank_name || ""}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{row.city || ""}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{row.check_no || ""}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{row.cv_no || ""}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        {(() => {
                          const statusBadge = getStatusBadgeMeta(row.status);
                          return (
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${statusBadge.className}`}
                            >
                              {statusBadge.label}
                            </span>
                          );
                        })()}
                      </td>
                      <td
                        className="px-2 py-1.5 whitespace-nowrap max-w-[220px] truncate"
                        title={row.deposit_slip_file || ""}
                      >
                        {row.deposit_slip_file || ""}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-right font-medium">
                        {formatCurrency(row.net_payable)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="px-3 py-2 border-t border-slate-200 flex items-center justify-between text-xs text-slate-600 flex-wrap gap-2">
            <span>
              Showing {selectedFrom} to {selectedTo} of {filteredSelectedRows.length} selected entries
              (from {selectedRows.length} total selected)
            </span>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setSelectedPage((prev) => Math.max(1, prev - 1))}
                disabled={selectedPage <= 1}
                className="px-2 py-1 border border-slate-300 rounded-lg disabled:opacity-50 hover:bg-slate-100"
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>

              <span className="px-2">
                Page {selectedPage} / {selectedTotalPages}
              </span>

              <button
                type="button"
                onClick={() => setSelectedPage((prev) => Math.min(selectedTotalPages, prev + 1))}
                disabled={selectedPage >= selectedTotalPages}
                className="px-2 py-1 border border-slate-300 rounded-lg disabled:opacity-50 hover:bg-slate-100"
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {isCorporateModalOpen && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-800">Select Corporates</h3>
                <p className="text-xs text-slate-500">
                  Choose one or more corporate codes to apply in filter.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsCorporateModalOpen(false)}
                className="h-8 w-8 rounded-full border border-slate-300 text-slate-500 hover:bg-slate-100"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="flex-1">
                  <div className="flex">
                    <input
                      value={corporateModalSearch}
                      onChange={(event) => setCorporateModalSearch(event.target.value)}
                      placeholder="Search corporate code or name"
                      className="w-full border border-slate-300 rounded-l-lg px-2 py-2 text-sm"
                    />
                    <span className="px-3 border border-l-0 border-slate-300 rounded-r-lg bg-white text-slate-500 flex items-center">
                      <FontAwesomeIcon icon={faSearch} />
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={selectAllFilteredCorporateDraft}
                  className="px-3 py-2 text-xs border border-slate-300 rounded-lg hover:bg-slate-100"
                >
                  Select Filtered
                </button>

                <button
                  type="button"
                  onClick={clearFilteredCorporateDraft}
                  className="px-3 py-2 text-xs border border-slate-300 rounded-lg hover:bg-slate-100"
                >
                  Clear Filtered
                </button>
              </div>

              <div className="mb-2 text-xs text-slate-600">
                Showing {filteredCorporateOptions.length} corporate option(s). Selected: {corporateDraftCodes.length}
              </div>

              <div className="border border-slate-200 rounded-lg max-h-[320px] overflow-auto">
                {filteredCorporateOptions.length === 0 && (
                  <div className="px-3 py-6 text-sm text-slate-400 text-center">No corporates found.</div>
                )}

                {filteredCorporateOptions.map((option) => (
                  <label
                    key={`corp-option-${option.value}-${option.label}`}
                    className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 last:border-b-0 cursor-pointer hover:bg-sky-50"
                  >
                    <input
                      type="checkbox"
                      checked={corporateDraftSet.has(option.value)}
                      onChange={() => toggleCorporateDraftCode(option.value)}
                    />
                    <span className="text-sm text-slate-700 truncate">{option.label}</span>
                    <span className="ml-auto text-[11px] px-2 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-600">
                      {option.value}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsCorporateModalOpen(false)}
                className="px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyCorporateSelection}
                className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Apply Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {!!confirmActionType && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200">
              <h3 className="text-sm font-bold text-slate-800">{confirmActionMeta.title}</h3>
              <p className="text-xs text-slate-500 mt-0.5">{confirmActionMeta.subtitle}</p>
            </div>

            <div className="p-4 text-sm text-slate-700 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="p-2 rounded-lg bg-slate-50 border border-slate-200">
                  <div className="text-xs text-slate-500">Selected PRNs</div>
                  <div className="text-sm font-semibold text-slate-800">{selectedSummary.count}</div>
                </div>

                <div className="p-2 rounded-lg bg-slate-50 border border-slate-200">
                  <div className="text-xs text-slate-500">Total Net Payable</div>
                  <div className="text-sm font-semibold text-slate-800">
                    {formatCurrency(selectedSummary.total)} | {selectedSummary.totalCompact}
                  </div>
                </div>

                <div className="p-2 rounded-lg bg-slate-50 border border-slate-200">
                  <div className="text-xs text-slate-500">Unique Corporates</div>
                  <div className="text-sm font-semibold text-slate-800">{selectedSummary.corpCount}</div>
                </div>

                <div className="p-2 rounded-lg bg-slate-50 border border-slate-200">
                  <div className="text-xs text-slate-500">Action</div>
                  <div className="text-sm font-semibold text-slate-800">{confirmActionMeta.confirmLabel}</div>
                </div>
              </div>

              <div>
                <div className="text-xs text-slate-500 mb-1">Sample PRNs</div>
                <div className="flex flex-wrap gap-1">
                  {selectedSummary.prnPreview.length === 0 && (
                    <span className="text-xs text-slate-400">No PRN preview available.</span>
                  )}
                  {selectedSummary.prnPreview.map((prn) => (
                    <span
                      key={`preview-prn-${prn}`}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-sky-50 text-sky-700 border border-sky-200"
                    >
                      {prn}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeConfirmAction}
                disabled={submittingConfirmAction || loadingGenerateType !== ""}
                className="px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-100 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitConfirmAction}
                disabled={submittingConfirmAction || loadingGenerateType !== ""}
                className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
              >
                {submittingConfirmAction || loadingGenerateType !== ""
                  ? "Processing..."
                  : confirmActionMeta.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditFilePage;
